<template>
  <div class="place-selection-draw-tab h-100">
    <div class="draw-tab-left">
      <div class="draw-form">
        <b-form-group label="Place Name">
          <b-form-input
            v-model="placeName"
            placeholder="Enter a name for this place"
            :state="nameState"
          />
          <b-form-invalid-feedback>A name is required before adding.</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="Address">
          <b-form-input v-model="address" placeholder="Enter an address" />
        </b-form-group>

        <b-form-group label="Category">
          <b-form-input v-model="category" placeholder="Enter Category" />
        </b-form-group>

        <b-form-group label="Brand">
          <b-form-input v-model="brand" placeholder="Enter Brand" />
        </b-form-group>

        <b-form-group label="Tags">
          <TagInput v-model="tags" />
        </b-form-group>

        <div class="draw-instructions mb-3">
          <small class="text-muted">
            Use the drawing tools on the map to draw one or more polygon shapes.
            Multiple shapes will be combined into a single MultiPolygon place.
          </small>
        </div>


        <b-alert v-if="drawError" variant="danger" show dismissible @dismissed="drawError = ''">
          {{ drawError }}
        </b-alert>

        <b-button
          variant="primary"
          @click="addFeature"
        >
          Add Place
        </b-button>
      </div>
    </div>

  </div>
</template>

<script>
import TagInput from '@/components/common/TagInput.vue';
import { isBlankName, mergeShapesToMultiPolygon } from './placeSelectionDrawHelpers';

export default {
  name: 'PlaceSelectionDrawTab',
  components: {
    TagInput,
  },
  props: {
    drawnShapes: {
      type: Array,
      default: () => [],
    },
    isSatelliteView: {
      type: Boolean,
      default: false,
    },
    geocodedAddress: {
      type: String,
      default: '',
    },
    geocodedName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      placeName: '',
      address: '',
      category: '',
      brand: '',
      tags: [],
      drawError: '',
      nameState: null,
    };
  },
  watch: {
    geocodedAddress(value) {
      if (!value) return;
      this.address = value;
    },
    // When a POI is selected from the shared map Geocoder, prefill the Place
    // Name only if the user hasn't already typed a name. Never overwrite an
    // existing (or whitespace-only-but-intentional) value.
    geocodedName(value) {
      if (!value) return;
      if (isBlankName(this.placeName)) {
        this.placeName = value;
      }
    },
  },
  methods: {
    addFeature() {
      if (this.drawnShapes.length === 0) {
        // No polygon drawn: prompt the user on the map with a transient
        // "Draw a polygon." warning that fades on its own, instead of a
        // persistent inline error.
        this.$emit('draw-command', { action: 'warn' });
        return;
      }

      // 9.4: reject blank/whitespace-only names without committing or mutating state.
      if (isBlankName(this.placeName)) {
        this.nameState = false;
        return;
      }
      this.nameState = null;

      // 9.5: merge all drawn shapes into a single MultiPolygon geometry.
      const geometry = mergeShapesToMultiPolygon(this.drawnShapes);

      // 9.6: commit via addSelectedPlace; on failure retain fields + shapes.
      try {
        this.$sessionStore.commit('addSelectedPlace', {
          _source: 'draw',
          name: this.placeName.trim(),
          address: this.address.trim() || null,
          category: this.category.trim() || null,
          brand: this.brand.trim() || null,
          labels: this.tags.length > 0 ? [...this.tags] : [],
          geometry,
        });
      } catch (err) {
        this.drawError = 'Unable to add place. Please try again.';
        return;
      }

      // 9.7/9.8: on success, reset fields and emit clear (resets shapes + history).
      this.placeName = '';
      this.address = '';
      this.category = '';
      this.brand = '';
      this.tags = [];
      this.nameState = null;
      this.drawError = '';
      this.$emit('draw-command', { action: 'clear' });
    },
  },
};
</script>

<style lang="scss" scoped>
.place-selection-draw-tab {
  height: 100%;
}

.draw-tab-left {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 1rem;

  .draw-form {
    display: flex;
    flex-direction: column;
  }

  .shape-row {
    display: flex;
    align-items: center;
    padding: 0.25rem 0;
    border-bottom: 1px solid #eee;
  }
}

.draw-panel-title {
    font-size: 16px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 14px;
}
</style>
